import axios from 'axios';
import oidcAuth from '../services/auth/oidc-client';

const DEFAULT_BASE_URL = 'http://localhost:4000/api/v1';

const baseURL = (import.meta as any).env?.VITE_CENTRAL_DB_API_URL
  ?? (import.meta as any).env?.VITE_API_BASE_URL
  ?? DEFAULT_BASE_URL;

export const centralDb = axios.create({
  baseURL: baseURL.replace(/\/$/, ''),
  withCredentials: false,
});

centralDb.interceptors.request.use(async (config) => {
  try {
    const token = await oidcAuth.getAccessToken();
    if (token) {
      const headers = (config.headers ?? {}) as any;
      headers.Authorization = `Bearer ${token}`;
      (config as any).headers = headers;
    }
  } catch {
    // ignore
  }
  return config;
});

export interface WorkerSummary {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  easyflexRegistrationNumber?: number;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export async function searchWorkers(params: { search?: string; limit?: number } = {}): Promise<WorkerSummary[]> {
  const { search, limit = 10000 } = params;
  const res = await centralDb.get<WorkerSummary[]>('/workers/options', {
    params: {
      limit,
      search: search || undefined,
    },
  });
  return Array.isArray(res.data) ? res.data : [];
}

export interface WorkedHoursBreakdownItem {
  year: number;
  month: number;
  hours: number;
}

export interface WorkedHoursResponseData {
  hours: number;
  breakdown: WorkedHoursBreakdownItem[];
  from?: string;
  to?: string;
  perOrganization?: Array<{
    organization: string;
    hours: number;
    breakdown: WorkedHoursBreakdownItem[];
  }>;
}

export interface WorkedHoursResponse {
  success: boolean;
  data: WorkedHoursResponseData;
  meta?: {
    workerId: string;
    easyflexRegistrationNumber?: number;
  };
  note?: string;
}

// Expose base URL and helper to build document URL
export const centralDbBaseUrl: string = (centralDb.defaults.baseURL || baseURL).replace(/\/$/, '');

export function buildWorkerIdDocumentUrl(easyflexRegistrationNumber: number): string {
  return `${centralDbBaseUrl}/easyflex/workers/${easyflexRegistrationNumber}/id-document.pdf`;
}

export async function getWorkerWorkedHours(workerId: string, params: { from?: string; to?: string } = {}): Promise<WorkedHoursResponseData> {
  const res = await centralDb.get<WorkedHoursResponse>(`/workers/${workerId}/worked-hours`, {
    params: {
      from: params.from || undefined,
      to: params.to || undefined,
    },
  });
  if (res.data && res.data.success && res.data.data) {
    return res.data.data;
  }
  // Fallback
  return { hours: 0, breakdown: [], from: params.from, to: params.to };
}

// ---------------- Travel API ----------------

export interface CreateTravelPayload {
  workerId: string;
  type: 'flight' | 'car_optima' | 'car_self';
  direction: 'leaving' | 'coming';
  flightNumber?: string;
  routeFrom?: string;
  routeTo?: string;
  departureAt?: string;
  arrivalAt?: string;
  ticketPrice?: number;
  privatePaidTicket?: boolean;
  ownContribution?: number;
  hoursForTicket?: number;
  inCreditsDebits?: boolean;
  creditsDebitsProcessedDate?: string;
  taronVoucherId?: string;
}

export async function listTravels(params: { workerId?: string; type?: string; direction?: string } = {}) {
  const res = await centralDb.get('/travels', { params });
  return res.data;
}

export async function createTravel(payload: CreateTravelPayload) {
  const res = await centralDb.post('/travels', payload);
  return res.data;
}

export async function updateTravel(id: string, payload: Partial<CreateTravelPayload>) {
  const res = await centralDb.patch(`/travels/${id}`, payload);
  return res.data;
}

export async function getTravel(id: string) {
  const res = await centralDb.get(`/travels/${id}`);
  return res.data;
}

// ---------------- Taron Vouchers API ----------------

export interface FlightCompanyOption {
  id?: string;
  code: string;
  name?: string;
  createdAt?: string;
}

export async function listFlightCompanies(): Promise<FlightCompanyOption[]> {
  const res = await centralDb.get<FlightCompanyOption[]>('/taron/flight-companies');
  return Array.isArray(res.data) ? res.data : [];
}

export async function createFlightCompany(payload: { code: string; name?: string }) {
  const res = await centralDb.post('/taron/flight-companies', payload);
  return res.data;
}

export interface TaronVoucher {
  id: string;
  code: string;
  direction?: 'inbound' | 'outbound';
  expiryDate?: string;
  createdAt: string;
  companyCode?: string;
  defaultPrice?: number;
  assignedTravelId?: string;
  assignedPersonName?: string;
  assignedFlightDate?: string;
  assignedAt?: string;
  active?: boolean;
  lastUnassignReason?: string;
  lastUnassignFinePaid?: boolean;
  lastUnassignFineParty?: 'worker' | 'optima';
  lastUnassignNote?: string;
  lastUnassignRecordedAt?: string;
}

export async function listVouchers(opts: { unassignedOnly?: boolean; assignedToTravelId?: string; direction?: 'inbound' | 'outbound' } = {}): Promise<TaronVoucher[]> {
  const res = await centralDb.get('/taron/vouchers', { params: { unassignedOnly: opts.unassignedOnly ? 'true' : undefined, assignedToTravelId: opts.assignedToTravelId || undefined, direction: opts.direction || undefined } });
  return res.data;
}

export async function bulkAddVouchers(
  codes: string[],
  expiryDate: string | undefined,
  direction: 'inbound' | 'outbound',
  companyCode?: string,
  defaultPrice?: number,
) {
  try {
    const res = await centralDb.post('/taron/vouchers/bulk', {
      codes,
      expiryDate,
      direction,
      companyCode,
      defaultPrice,
    });
    return res.data;
  } catch (err: any) {
    const msg = err?.response?.data?.message;
    const complainsAboutDirection = Array.isArray(msg)
      ? msg.some((m) => typeof m === 'string' && m.toLowerCase().includes('direction'))
      : typeof msg === 'string' && msg.toLowerCase().includes('direction');
    if (complainsAboutDirection) {
      const res = await centralDb.post('/taron/vouchers/bulk', { codes, expiryDate, companyCode, defaultPrice });
      return res.data;
    }
    throw err;
  }
}

export async function assignVoucher(id: string, payload: { travelId: string; personName?: string; flightDate?: string }) {
  const res = await centralDb.patch(`/taron/vouchers/${id}/assign`, payload);
  return res.data;
}

export async function unassignVoucher(id: string, payload: { reason: string; finePaid: boolean; fineParty: 'worker' | 'optima'; note?: string; active: boolean }) {
  const res = await centralDb.patch(`/taron/vouchers/${id}/unassign`, payload);
  return res.data;
}

// ---------------- Travel Settings API ----------------

export interface TravelSettings {
  hoursPerTicket: number;
  carAllowanceEuro: number;
}

export async function getTravelSettings(): Promise<TravelSettings> {
  const res = await centralDb.get('/admin/travel-settings');
  return res.data;
}

export async function patchTravelSettings(settings: Partial<TravelSettings>): Promise<TravelSettings> {
  const res = await centralDb.patch('/admin/travel-settings', settings);
  return res.data;
}

// ---------------- Allowance Hours (Admin) ----------------

export interface AllowanceHoursSyncResult {
  success: boolean;
  from: string;
  months: string[];
  totalWorkers: number;
  processed: number;
  created: number;
  updated: number;
}

export async function syncAllowanceHours(from?: string): Promise<AllowanceHoursSyncResult> {
  const res = await centralDb.post('/admin/allowance-hours/sync', null, { params: { from: from || undefined } });
  return res.data;
}

export async function getAllowanceHoursSummary(from?: string): Promise<{ from: string; months: string[]; counts: Array<{ period_start: string; count: string }> }> {
  const res = await centralDb.get('/admin/allowance-hours/summary', { params: { from: from || undefined } });
  return res.data;
}

export interface AllowanceHoursByWorkerRow {
  workerId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  easyflexRegistrationNumber?: number | null;
  monthsCount: number;
  fromPeriod: string; // date
  toPeriod: string;   // date
  lastUpdatedAt: string; // timestamp
  usedHours?: number; // total hours_for_ticket in period
}

export async function getAllowanceHoursByWorker(from?: string): Promise<AllowanceHoursByWorkerRow[]> {
  const res = await centralDb.get('/admin/allowance-hours/by-worker', { params: { from: from || undefined } });
  return Array.isArray(res.data) ? res.data : [];
}
